// @flow

import React from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import moment from "moment";
import { GSGrid, StatTileGroup } from "@components";
import type { RtwInterviewHistoryQuestionAnswer } from "@modules/shared/reducer/employee-absences/types";
import { formatDateTime } from "@transformers/date";
import type { RtwInterviewHistory } from "@modules/shared/reducer/employee-absences/types";
import { formatQuestionAnswerResponse } from "@modules/shared/utils/question-answer-response";

type OwnProps = $Exact<{
  data: RtwInterviewHistory,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class AbsenceRTWInterviewView extends React.Component<Props> {
  get timeToCompleteInterview() {
    const { completionDate, rtwAt } = this.props.data || {};

    if (!completionDate || !rtwAt) {
      return this.props.t("N/A");
    }

    return `${moment(completionDate).diff(moment(rtwAt), "hours")} Hours`;
  }

  get interviewStatus() {
    const { completionDate, dueDate } = this.props.data || {};

    if (!completionDate || !dueDate) {
      return {
        title: this.props.t("N/A"),
        className: "small",
      };
    }

    return moment(completionDate).isAfter(moment(dueDate))
      ? {
          value: this.props.t("COMPLETED_LATE"),
          className: "small",
          footerClassName: "border-bottom-8px-negative",
        }
      : {
          value: this.props.t("COMPLIANT"),
          className: "small",
          footerClassName: "border-bottom-8px-mint-d1",
        };
  }

  get interviewDetailsColumns() {
    return [
      {
        title: this.props.t("DATE_COMPLETED"),
        value: formatDateTime(this.props.data && this.props.data.completionDate),
        className: "small",
      },
      {
        title: this.props.t("COMPLETED_BY"),
        value: this.props.data && (this.props.data.completedBy || { name: "" }).name,
        className: "small",
      },
      {
        title: this.props.t("TIME_TO_COMPLETE"),
        value: this.timeToCompleteInterview,
        className: "small",
      },
      {
        title: this.props.t("STATUS"),
        ...this.interviewStatus,
      },
    ];
  }

  get questionsColumns() {
    return [
      {
        name: this.props.t("QUESTION"),
        key: "question",
        align: "left",
        width: "60%",
        cellClass: "p-0",
        renderCell: ({ row }: { row: RtwInterviewHistoryQuestionAnswer }) => (
          <div
            className={classNames("grid-text-cell px-2", {
              "bg-light-grey": row.question.questionType === "CONFIRMATION",
            })}
          >
            {(row.question || {}).questionText || (row.question || {}).question}
          </div>
        ),
      },
      {
        name: this.props.t("ANSWER"),
        cellClass: "p-0",
        renderCell: ({ row }: { row: RtwInterviewHistoryQuestionAnswer }) => (
          <div
            className={classNames("grid-text-cell px-2", {
              "bg-light-grey": row.question.questionType === "CONFIRMATION",
            })}
          >
            {formatQuestionAnswerResponse(row.answer, this.props.t)}
          </div>
        ),
        key: "employeeResponse",
        align: "left",
      },
    ];
  }

  render() {
    return (
      <div>
        {this.props.data &&
        this.props.data.completionDate &&
        this.props.data.completedBy &&
        this.props.data.completedBy.name ? (
          <StatTileGroup data={this.interviewDetailsColumns} className="mb-4" />
        ) : null}

        <GSGrid
          rows={(this.props.data || { questionAnswers: [] }).questionAnswers}
          columns={this.questionsColumns}
          rowHeight={() => {
            return 80;
          }}
        />
      </div>
    );
  }
}

export default localize()(AbsenceRTWInterviewView);
